import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  CustomInput,
  CustomSelect,
  CustomTextArea,
  GridSectionWrapper,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import PictureUploadComponent from 'src/consumer/components/Helpers/PictureUploadComponent/PictureUploadComponent'
import { genericHandleChange } from 'src/consumer/helpers/utilFunctions'
import { getDataWithParams, postDataWithParams } from 'src/consumer/services/profileService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const countryOptions = [
  { value: 'IN', label: 'India' },
  { value: 'US', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  // Add more countries as needed
]

const entityTypeOptions = [
  { value: 'mentor', label: 'Mentor' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'company', label: 'Company' },
]

const paymentCycleOptions = [
  { id: 'MONTHLY', title: 'Monthly' },
  { id: 'WEEKLY', title: 'Weekly' },
  { id: 'BIWEEKLY', title: 'Bi-weekly' },
  { id: 'CUSTOM', title: 'Custom' },
]

const paymentCycleOptionIds = new Set(paymentCycleOptions.map((option) => option.id))

function getDefaultCycleLength(paymentCycle) {
  if (paymentCycle === 'WEEKLY') {
    return 7
  }
  if (paymentCycle === 'BIWEEKLY') {
    return 14
  }
  return 30
}

function normalizePaymentCycleData(paymentConfigData = {}) {
  const rawPaymentCycle = paymentConfigData?.paymentCycle
  const paymentCycle = paymentCycleOptionIds.has(rawPaymentCycle)
    ? rawPaymentCycle
    : rawPaymentCycle
      ? 'CUSTOM'
      : 'MONTHLY'
  const startDay = Number(paymentConfigData?.paymentCycleStartDayOfMonth)

  return {
    ...paymentConfigData,
    paymentCycle,
    customPaymentCycleLabel:
      paymentConfigData?.customPaymentCycleLabel ||
      (!paymentCycleOptionIds.has(rawPaymentCycle) ? rawPaymentCycle || '' : ''),
    paymentCycleLengthInDays:
      paymentConfigData?.paymentCycleLengthInDays || getDefaultCycleLength(paymentCycle),
    paymentCycleStartDayOfMonth: Number.isFinite(startDay)
      ? Math.max(1, Math.min(Math.trunc(startDay), 31))
      : 1,
  }
}

function isBlankPaymentValue(value) {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
}

function applyPaymentPrefill(baseData, prefillData = {}) {
  const next = { ...baseData }
  Object.entries(prefillData || {}).forEach(([key, value]) => {
    if (!isBlankPaymentValue(value) && isBlankPaymentValue(next[key])) {
      next[key] = value
    }
  })
  return next
}

function applySavedPaymentConfig(baseData, paymentConfigData = {}) {
  const next = { ...baseData }
  Object.entries(paymentConfigData || {}).forEach(([key, value]) => {
    if (!isBlankPaymentValue(value)) {
      next[key] = value
    }
  })
  return next
}

/** Portal dropdowns to body so they don't clip or overlap inside the drawer */
const SELECT_MENU_PROPS = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  menuPosition: 'fixed',
}

function getPaymentConfigApiBase() {
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname
    if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
      return '/api/secure/cc/support/invoices/payment-config'
    }
  }
  return '/api/secure/l3/invoices/payment-config'
}

export default function PaymentConfigForm({
  entityId,
  entityType,
  prefillData,
  hideEntitySection = false,
  inDialog: _inDialog = false,
  onSaved,
  onClose,
}) {
  const [formData, setFormData] = useState({
    entityId: entityId || '',
    entityType: entityType || '',
    contactName: '',
    contactMobile: '',
    contactEmail: '',
    name: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    country: 'IN',
    gstVat: '',
    bankName: '',
    bankBranchName: '',
    bankAddress: '',
    nextPaymentInDays: 7,
    accountNumber: '',
    ifscCode: '',
    panCardNumber: '',
    upiId: '',
    swiftCode: '',
    iban: '',
    logoId: '',
    tdsPercentage: '',
    paymentCycle: 'MONTHLY',
    paymentCycleLengthInDays: 30,
    paymentCycleStartDayOfMonth: 1,
    customPaymentCycleLabel: '',
    adminIntimationDaysAfterCycle: 1,
    practitionerIntimationDaysAfterCycle: 2,
    transferInitiationDaysAfterCycle: 3,
    transferCompletionDaysAfterCycle: 5,
  })

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [errors, setErrors] = useState({})

  // Fetch existing payment config
  const fetchPaymentConfig = useCallback(async () => {
    if (!entityId || !entityType) return

    try {
      setLoading(true)
      const response = await getDataWithParams(`${getPaymentConfigApiBase()}/get`, {
        entityId,
        entityType,
      })

      if (response?.status === 200 && response?.data) {
        if (isCommonResponseSuccessfulV2(response)) {
          const paymentConfigData = getDataMapFromCommonResponse(response)?.['payment-config']
          setFormData((prevFormData) =>
            normalizePaymentCycleData(
              applySavedPaymentConfig(prevFormData, paymentConfigData),
            ),
          )
        } else if (response?.data?.code !== 48961) {
          throw new Error(response?.data?.message)
        }
      }
    } catch (error) {
      console.error('Error fetching payment config:', error)
      toast.error('Failed to load payment configuration')
    } finally {
      setLoading(false)
      setInitialLoad(false)
    }
  }, [entityId, entityType])

  const handleChange = (e) => {
    genericHandleChange(e, setFormData)
  }

  // Validate cycle fields
  const validateCycleFields = () => {
    const newErrors = {}

    // If custom cycle is selected, paymentCycleLengthInDays is required
    if (formData.paymentCycle === 'CUSTOM' && !formData.paymentCycleLengthInDays) {
      newErrors.paymentCycleLengthInDays = 'Cycle length is required for custom cycles'
    }

    // Validate transfer completion >= transfer initiation
    if (formData.transferCompletionDaysAfterCycle < formData.transferInitiationDaysAfterCycle) {
      newErrors.transferCompletionDaysAfterCycle =
        'Transfer completion must be after or equal to transfer initiation'
    }

    // Validate paymentCycleStartDayOfMonth (1-31)
    if (
      formData.paymentCycleStartDayOfMonth !== undefined &&
      formData.paymentCycleStartDayOfMonth !== null &&
      (formData.paymentCycleStartDayOfMonth < 1 || formData.paymentCycleStartDayOfMonth > 31)
    ) {
      newErrors.paymentCycleStartDayOfMonth = 'Must be between 1 and 31'
    }

    // Validate non-negative integers
    const offsetFields = [
      'adminIntimationDaysAfterCycle',
      'practitionerIntimationDaysAfterCycle',
      'transferInitiationDaysAfterCycle',
      'transferCompletionDaysAfterCycle',
    ]

    offsetFields.forEach((field) => {
      if (formData[field] < 0) {
        newErrors[field] = 'Must be a non-negative number'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setSaving(true)

      // Basic validation
      if (!formData.contactName || !formData.contactEmail || !formData.name) {
        toast.error('Please fill in all required fields')
        setSaving(false)
        return
      }

      // Validate cycle fields
      if (!validateCycleFields()) {
        toast.error('Please fix validation errors')
        setSaving(false)
        return
      }

      // Prepare payload with stable cycle IDs. Custom labels are stored separately.
      const payload = {
        ...formData,
        apiParam: 'payment-config',
      }

      const response = await postDataWithParams(`${getPaymentConfigApiBase()}/save`, payload)

      if (response?.status === 200) {
        toast.success('Payment configuration saved successfully')
        // Call the optional onSaved callback
        if (onSaved) {
          onSaved(getDataMapFromCommonResponse(response)?.['payment-config'] || payload)
        }
      } else {
        toast.error('Failed to save payment configuration')
      }
    } catch (error) {
      console.error('Error saving payment config:', error)
      toast.error('Failed to save payment configuration')
    } finally {
      setSaving(false)
    }
  }

  // Load data on component mount
  useEffect(() => {
    fetchPaymentConfig()
  }, [fetchPaymentConfig])

  useEffect(() => {
    setFormData((prevFormData) =>
      normalizePaymentCycleData(applyPaymentPrefill(prevFormData, prefillData)),
    )
  }, [prefillData])

  const theme = useTheme()

  function renderForm() {
    if (initialLoad && loading) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(6, 0),
            gap: theme.spacing(2),
          }}
        >
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Loading payment configuration…
          </Typography>
        </div>
      )
    }
    return (
      <Grid container spacing={3}>
        {/* Entity (when not hidden) */}
        {!hideEntitySection && (
          <GridSectionWrapper
            shadow={false}
            heading="Entity"
            sectionContainerSx={{ marginBottom: theme.spacing(4) }}
          >
            <Grid item xs={12}>
              <CustomInput
                id="entityId"
                label="Entity ID"
                value={formData.entityId}
                onChange={handleChange}
                required
                disabled={!!entityId}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomSelect
                id="entityType"
                label="Entity Type"
                value={formData.entityType}
                onChangeEvent={handleChange}
                options={entityTypeOptions}
                selectType={selectTypes.string}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                required
                disabled={!!entityType}
                {...SELECT_MENU_PROPS}
              />
            </Grid>
          </GridSectionWrapper>
        )}

        {/* Contact & legal */}
        <GridSectionWrapper
          shadow={false}
          heading="Contact & legal name"
          sectionContainerSx={{ marginBottom: theme.spacing(4) }}
        >
          <Grid item xs={12}>
            <CustomInput
              id="contactName"
              label="Contact name"
              value={formData.contactName}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="contactMobile"
              label="Contact mobile"
              value={formData.contactMobile}
              onChange={handleChange}
              type="tel"
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="contactEmail"
              label="Contact email"
              value={formData.contactEmail}
              onChange={handleChange}
              type="email"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="name"
              label="Company / legal name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Grid>
        </GridSectionWrapper>

        {/* Address */}
        <GridSectionWrapper
          shadow={false}
          heading="Address"
          sectionContainerSx={{ marginBottom: theme.spacing(4) }}
        >
          <Grid item xs={12}>
            <CustomTextArea
              id="address"
              label="Street address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput id="city" label="City" value={formData.city} onChange={handleChange} />
          </Grid>
          <Grid item xs={12}>
            <CustomInput id="state" label="State" value={formData.state} onChange={handleChange} />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="pinCode"
              label="PIN / ZIP"
              value={formData.pinCode}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="payment-config-country-label">Country</InputLabel>
              <Select
                labelId="payment-config-country-label"
                id="country"
                label="Country"
                value={formData.country || 'IN'}
                onChange={(event) => {
                  setFormData((prev) => ({
                    ...prev,
                    country: event.target.value || 'IN',
                  }))
                }}
              >
                {countryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </GridSectionWrapper>

        {/* Tax (GST / PAN) */}
        <GridSectionWrapper
          shadow={false}
          heading="Tax (GST / PAN)"
          sectionContainerSx={{ marginBottom: theme.spacing(4) }}
        >
          <Grid item xs={12}>
            <CustomInput
              id="gstVat"
              label="GST / VAT number"
              value={formData.gstVat}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="panCardNumber"
              label="PAN number"
              value={formData.panCardNumber}
              onChange={handleChange}
            />
          </Grid>
        </GridSectionWrapper>

        {/* Bank details */}
        <GridSectionWrapper
          shadow={false}
          heading="Bank details"
          sectionContainerSx={{ marginBottom: theme.spacing(4) }}
        >
          <Grid item xs={12}>
            <CustomInput
              id="bankName"
              label="Bank name"
              value={formData.bankName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="bankBranchName"
              label="Branch name"
              value={formData.bankBranchName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              id="bankAddress"
              label="Bank address"
              value={formData.bankAddress}
              onChange={handleChange}
              rows={3}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="accountNumber"
              label="Account number"
              value={formData.accountNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="ifscCode"
              label="IFSC code"
              value={formData.ifscCode}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="swiftCode"
              label="SWIFT code"
              value={formData.swiftCode}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="tdsPercentage"
              label="TDS (%)"
              value={formData.tdsPercentage}
              onChange={(e) => {
                if (e.target.value < 0) {
                  e.target.value = 0
                }
                if (e.target.value > 100) {
                  e.target.value = 100
                }
                handleChange(e)
              }}
              type="number"
              pureInputProps={{
                min: '0',
                max: '100',
                step: '0.1',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput id="iban" label="IBAN" value={formData.iban} onChange={handleChange} />
          </Grid>
        </GridSectionWrapper>

        {/* Payment & UPI */}
        <GridSectionWrapper
          shadow={false}
          heading="Payment & UPI"
          sectionContainerSx={{ marginBottom: theme.spacing(4) }}
        >
          <Grid item xs={12}>
            <CustomInput
              id="upiId"
              label="UPI ID"
              value={formData.upiId}
              onChange={handleChange}
              placeholder="example@upi"
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="nextPaymentInDays"
              label="Next payment (days)"
              value={formData.nextPaymentInDays}
              onChange={handleChange}
              type="number"
              min="1"
            />
          </Grid>
        </GridSectionWrapper>

        {/* Payment cycle */}
        <GridSectionWrapper
          shadow={false}
          heading="Payment cycle"
          sectionContainerSx={{ marginBottom: theme.spacing(4) }}
        >
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="payment-cycle-label">Cycle</InputLabel>
              <Select
                labelId="payment-cycle-label"
                id="paymentCycle"
                label="Cycle"
                value={formData.paymentCycle || 'MONTHLY'}
                onChange={(e) => {
                  const nextCycle = e.target.value || 'MONTHLY'
                  setFormData((prev) => ({
                    ...prev,
                    paymentCycle: nextCycle,
                    paymentCycleLengthInDays: getDefaultCycleLength(nextCycle),
                  }))
                }}
              >
                {paymentCycleOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Set the cycle used to default payout report periods.
              </FormHelperText>
            </FormControl>
          </Grid>
          {formData.paymentCycle && (
            <>
              <Grid item xs={12}>
                <CustomInput
                  id="paymentCycleLengthInDays"
                  label="Cycle Length (Days)"
                  value={formData.paymentCycleLengthInDays}
                  onChange={handleChange}
                  type="number"
                  min="1"
                  required={formData.paymentCycle === 'CUSTOM'}
                  error={!!errors.paymentCycleLengthInDays}
                  helperText={
                    errors.paymentCycleLengthInDays ||
                    (formData.paymentCycle === 'CUSTOM'
                      ? 'Required for custom cycle'
                      : 'Days in this cycle')
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <CustomInput
                  id="paymentCycleStartDayOfMonth"
                  label="Cycle start day of month (1–31)"
                  value={formData.paymentCycleStartDayOfMonth}
                  onChange={handleChange}
                  type="number"
                  min="1"
                  max="31"
                  error={!!errors.paymentCycleStartDayOfMonth}
                  helperText={
                    errors.paymentCycleStartDayOfMonth ||
                    'e.g. 16 = cycle runs 16th → 15th next month'
                  }
                />
              </Grid>
            </>
          )}
          {formData.paymentCycle === 'CUSTOM' && (
            <Grid item xs={12}>
              <CustomInput
                id="customPaymentCycleLabel"
                label="Custom cycle label (optional)"
                value={formData.customPaymentCycleLabel || ''}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    customPaymentCycleLabel: e.target.value,
                    paymentCycle: 'CUSTOM',
                  }))
                }}
                placeholder="e.g. CUSTOM_45D"
              />
            </Grid>
          )}
        </GridSectionWrapper>

        {/* Cycle timing (days after cycle end) */}
        <GridSectionWrapper
          shadow={false}
          heading="Cycle timing (days after cycle end)"
          sectionContainerSx={{ marginBottom: theme.spacing(4) }}
        >
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When to notify and initiate transfers
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="adminIntimationDaysAfterCycle"
              label="Admin intimation (days)"
              value={formData.adminIntimationDaysAfterCycle}
              onChange={handleChange}
              type="number"
              min="0"
              error={!!errors.adminIntimationDaysAfterCycle}
              helperText={errors.adminIntimationDaysAfterCycle || 'Notify admin/support'}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="practitionerIntimationDaysAfterCycle"
              label="Practitioner intimation (days)"
              value={formData.practitionerIntimationDaysAfterCycle}
              onChange={handleChange}
              type="number"
              min="0"
              error={!!errors.practitionerIntimationDaysAfterCycle}
              helperText={errors.practitionerIntimationDaysAfterCycle || 'Notify practitioner'}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="transferInitiationDaysAfterCycle"
              label="Transfer initiation (days)"
              value={formData.transferInitiationDaysAfterCycle}
              onChange={handleChange}
              type="number"
              min="0"
              error={!!errors.transferInitiationDaysAfterCycle}
              helperText={errors.transferInitiationDaysAfterCycle || 'Start bank transfer'}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="transferCompletionDaysAfterCycle"
              label="Transfer completion (days)"
              value={formData.transferCompletionDaysAfterCycle}
              onChange={handleChange}
              type="number"
              min="0"
              error={!!errors.transferCompletionDaysAfterCycle}
              helperText={errors.transferCompletionDaysAfterCycle || 'Funds expected'}
            />
          </Grid>
        </GridSectionWrapper>

        {/* Logo */}
        <GridSectionWrapper
          shadow={false}
          heading="Company logo"
          sectionContainerSx={{ marginBottom: 0 }}
        >
          <Grid item xs={12}>
            <PictureUploadComponent
              id="logoId"
              value={formData.logoId}
              onChange={handleChange}
              title="Upload Company Logo"
              aspectWidth={200}
              aspectHeight={200}
            />
          </Grid>
        </GridSectionWrapper>
      </Grid>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: 720,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{renderForm()}</div>
      <Paper
        elevation={0}
        square
        sx={{
          p: 2.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: theme.spacing(2),
          flexShrink: 0,
          boxShadow: '0 -2px 12px rgba(0,0,0,0.04)',
        }}
      >
        {onClose && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={saving}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: 1.5,
              '&:hover': { borderWidth: 1.5 },
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 160,
            py: 1,
            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            '&:hover': {
              boxShadow: `0 4px 12px ${theme.palette.primary.main}50`,
            },
          }}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save configuration'}
        </Button>
      </Paper>
    </div>
  )
}
